import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { QueueConfirmDialog } from './queueActions';
import { useActionQueueDescriptors, type QueueDescriptor } from './useActionQueueDescriptors';

/**
 * The ranked action queue — one ordered list that replaces the old alert band's
 * separate surfaces: the rapid-report banner, the daily-report banner, the
 * amendment nudge, AND the overdue filing rows that used to live inside the
 * Upcoming-filings card. Every item keeps the exact mutation it had before —
 * this is a re-presentation, not new behavior.
 *
 * Membership rule (settled in the grill session): an item queues iff it has an
 * action. Fixed ranking:
 *   1. OVERDUE rapid reports — rose "FILE NOW" + Generate + "filed elsewhere"
 *      dismiss (the existing attestation).
 *   2. the owed daily report — Mark-filed-in-portal. The standing AL
 *      daily-window explainer sentence rides HERE as this item's detail (the
 *      old standalone blue strip is deleted).
 *   3. NON-OVERDUE rapid reports (imminent/upcoming) — amber "DUE SOON", same
 *      Generate / mark-reported / attestation-dismiss actions as the overdue
 *      ones. These used to live in RapidReportAlertStrip; the queue must show
 *      EVERY rapid obligation, not only the overdue slice.
 *   4. the amendment nudge (reporting threshold crossed) — its review link.
 *
 * TWO display states only — collapsed / expanded:
 *   • collapsed (default, 2+ items): the WHOLE STACK is one expand target — a
 *     div with role="button", tabIndex 0, aria-expanded=false and a label naming
 *     how many more wait; click / Enter / Space all expand. The front card
 *     renders in full but its content is pointer-inert and its action buttons
 *     are tabIndex=-1 + aria-hidden, so the entire tinted card reads as "click
 *     to expand" (a click that lands on a button falls through to the wrapper).
 *     A permanent "+N more" chip sits in the card's top row. Directly under the
 *     card sit one or two DECORATIVE peek edges (plain flow divs) hinting at the
 *     deck. A hover/focus tint overlay (the only absolute positioning) fades in
 *     over the card. The buried cards are NOT in the DOM while collapsed.
 *   • expanded (click / Enter / Space, or `?action=rapid` deep-link): the front
 *     card becomes fully interactive again, a quiet "Showing all — collapse"
 *     strip appears, and the remaining cards render below it IN FLOW — page
 *     content below simply moves down. Each entering card animates in (opacity +
 *     translateY, staggered). Collapse unmounts them with no exit choreography.
 *
 * Why the peek edges are plain flow divs, not real positioned card clones: two
 * earlier "Sonner deck" attempts stacked absolutely-positioned scaled/clipped
 * card clones behind the front card, and BOTH rendered broken in the real
 * browser. In-flow content can't fake a fixed-overlay stack. So the deck illusion
 * is drawn with a couple of quiet in-flow slivers that can't break, and the ONE
 * piece of absolute positioning left is the hover/focus tint inside the card.
 *
 * `?action=rapid` deep-link: when set AND at least one rapid item is queued, the
 * queue initializes EXPANDED so the linked item is visible without a click.
 *
 * Motion + the hover tint are transform/opacity only, kept LOCAL to this
 * component (inline <style>) so they never touch index.css. The hover reveal is
 * gated behind `@media (hover: hover) and (pointer: fine)` so touch devices don't
 * get a stuck tint; the :focus-visible reveal is NOT gated (keyboard focus works
 * everywhere). Reduced-motion drops the entrance to an opacity-only fade and
 * keeps the reduced-motion promote override.
 */

const EXIT_MS = 150;
/** Per-card stagger for the expand entrance, in ms. */
const ENTER_STAGGER_MS = 40;

type QueueState = 'collapsed' | 'expanded';

export function ActionQueue() {
  const { descriptors, forceRapid, unready } = useActionQueueDescriptors();

  // `?action=rapid` opens the queue so the deep-linked item is visible without
  // a click. The lazy initializer covers the warm-cache case, but on a COLD
  // load the obligations query is still in flight during the first render, so
  // the initializer alone loses the race and the deep link lands collapsed —
  // the effect below re-opens when the rapid item arrives. The ref keeps it
  // from fighting a collapse the user made on purpose.
  const hasRapidDescriptor = descriptors.some((d) => d.isRapid);
  const [expanded, setExpanded] = useState(() => forceRapid && hasRapidDescriptor);
  const userToggledRef = useRef(false);
  useEffect(() => {
    if (forceRapid && hasRapidDescriptor && !userToggledRef.current) setExpanded(true);
  }, [forceRapid, hasRapidDescriptor]);
  const [exitingId, setExitingId] = useState<string | null>(null);
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(() => new Set());
  // The id that most recently became the front card AS A RESULT OF a resolve.
  // Drives the one-shot promote entrance. Never set on initial page load, so the
  // first paint is still (page-load choreography stays banned).
  const [promotedId, setPromotedId] = useState<string | null>(null);

  const visible = useMemo(
    () => descriptors.filter((d) => !resolvedIds.has(d.id)),
    [descriptors, resolvedIds],
  );

  const resolve = (id: string) => {
    // The card that THIS resolve promotes, decided at click time: the first
    // still-visible item once `id` drops out. Naming it here rather than
    // inferring it from a later render is what keeps the promote entrance tied
    // to a resolve — a background refetch that reorders the queue is not one.
    const promoted = visible.find((d) => d.id !== id)?.id ?? null;
    setExitingId(id);
    // Let the exit transition play, then drop the item so the next promotes.
    setTimeout(() => {
      setExitingId(null);
      setResolvedIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });
      setPromotedId(promoted);
    }, EXIT_MS);
  };

  if (visible.length === 0) {
    return unready ? (
      <p role="alert" data-testid="action-queue-unready" className="text-[13px] text-rose-700">
        {unready}
      </p>
    ) : null;
  }

  const [front, ...rest] = visible;
  const hiddenCount = rest.length;
  const hasRest = hiddenCount > 0;
  const state: QueueState = expanded && hasRest ? 'expanded' : 'collapsed';
  // The collapsed deck (whole-stack expand target + inert front card + peek
  // edges + tint) only exists when there's something buried AND we're collapsed.
  const isDeck = hasRest && state === 'collapsed';

  const frontCard = (
    <QueueCard
      descriptor={front}
      isFront
      inert={isDeck}
      hiddenCount={hiddenCount}
      exiting={exitingId === front.id}
      promoting={front.id === promotedId}
      entering={false}
      enterIndex={0}
      onResolve={resolve}
      onPromoteEnd={() => setPromotedId(null)}
    />
  );

  return (
    <div data-testid="action-queue" data-state={state} className="action-queue flex flex-col gap-2">
      <style>{QUEUE_STYLE}</style>

      {/* Neutral section heading + count. Neutral on purpose: the queue is
          heterogeneous (overdue "FILE NOW" alongside not-yet-overdue "DUE SOON"
          and the amendment nudge), so a rose "needs filing now" header would
          over-claim urgency for the non-overdue items. Per-card tags carry it. */}
      <div
        data-testid="action-queue-heading"
        className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[.08em] font-mono text-slate-500"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden />
        Needs your attention · {visible.length}
      </div>

      {isDeck ? (
        // Collapsed deck: the entire stack is ONE expand target. A div acting as
        // a button (role + tabIndex + key handling) rather than a real <button>
        // so the front card's own buttons aren't nested button-in-button. Click,
        // Enter and Space all expand.
        <div
          role="button"
          tabIndex={0}
          aria-expanded={false}
          aria-label={`Expand — ${hiddenCount} more waiting`}
          onClick={() => {
            userToggledRef.current = true;
            setExpanded(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              userToggledRef.current = true;
              setExpanded(true);
            }
          }}
          className="action-queue-deck group relative block w-full cursor-pointer rounded-xl text-left focus:outline-none"
        >
          {frontCard}
          {/* Decorative peek edges — plain in-flow divs, NOT real cards, so they
              cannot break the way the abandoned positioned clones did. One edge
              when a single item is hidden, two when 2+ are. They sit flush under
              the card (negative top margin closes the gap-2). */}
          <div
            data-testid="action-queue-edge"
            aria-hidden
            className="-mt-2 mx-3 h-2.5 rounded-b-xl border-x border-b border-slate-200 bg-white shadow-sm"
          />
          {hiddenCount > 1 && (
            <div
              data-testid="action-queue-edge"
              aria-hidden
              className="mx-6 h-2 rounded-b-xl border-x border-b border-slate-200 bg-white/90"
            />
          )}
        </div>
      ) : (
        // Single item, or expanded: the front card is a plain interactive card.
        frontCard
      )}

      {/* Expanded: the quiet "collapse" strip + the remaining cards render below,
          in flow — page content simply moves down. A real <button> so it's
          focusable and Enter/Space toggle it natively. */}
      {state === 'expanded' && (
        <>
          <button
            type="button"
            data-testid="action-queue-toggle"
            aria-expanded
            onClick={() => {
              userToggledRef.current = true;
              setExpanded(false);
            }}
            className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-2.5 text-left text-[13px] text-slate-600 hover:bg-slate-100"
          >
            <span className="font-medium">Showing all — collapse</span>
            <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          </button>

          {rest.map((d, idx) => (
            <QueueCard
              key={d.id}
              descriptor={d}
              isFront={false}
              inert={false}
              hiddenCount={0}
              exiting={exitingId === d.id}
              promoting={false}
              entering
              enterIndex={idx}
              onResolve={resolve}
            />
          ))}
        </>
      )}
    </div>
  );
}

function QueueCard({
  descriptor,
  isFront,
  inert,
  hiddenCount,
  exiting,
  promoting,
  entering,
  enterIndex,
  onResolve,
  onPromoteEnd,
}: {
  descriptor: QueueDescriptor;
  isFront: boolean;
  /** Collapsed-deck front card: content is pointer-inert, action + dismiss
   *  buttons drop out of the tab order + hide from AT, and the count chip + tint
   *  overlay render. A click that lands anywhere falls through to the wrapper. */
  inert: boolean;
  /** How many items are buried behind this card — drives the "+N more" chip and
   *  the tint copy. Only meaningful when `inert`. */
  hiddenCount: number;
  exiting: boolean;
  /** Plays the one-shot promote entrance — set only when this card became the
   *  front AS A RESULT OF a resolve (never on initial page load). */
  promoting: boolean;
  /** Plays the expand entrance (opacity + translateY, staggered by index). */
  entering: boolean;
  /** Position among the entering cards — drives the stagger delay. */
  enterIndex: number;
  onResolve: (id: string) => void;
  /** Clears the promote flag when its animation completes — without this, the
   *  card REPLAYS the entrance every time expand/collapse remounts it under a
   *  different parent (deck wrapper vs direct child). One-shot means one shot. */
  onPromoteEnd?: () => void;
}) {
  const toneBorder = descriptor.tone === 'amber' ? 'border-t-amber-500' : 'border-t-rose-500';
  const labelTone = descriptor.tone === 'amber' ? 'text-amber-600' : 'text-rose-600';
  const resolve = () => onResolve(descriptor.id);
  const actionNode = descriptor.renderAction(resolve, inert);
  // The dismiss × is a committing "filed elsewhere" attestation — it always
  // confirms (ADR-023). The × only opens the dialog; `dismiss.onAccept` fires
  // on accept.
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false);

  return (
    <div
      data-testid={`action-queue-item-${descriptor.id}`}
      className={cn(
        'action-queue-card relative w-full rounded-xl border border-slate-200 bg-white p-4 border-t-[3px] shadow-sm',
        toneBorder,
        exiting && 'action-queue-card-exiting',
        promoting && 'action-queue-card-promoting',
        entering && 'action-queue-card-entering',
      )}
      style={entering ? ({ '--enter-delay': `${enterIndex * ENTER_STAGGER_MS}ms` } as React.CSSProperties) : undefined}
      onAnimationEnd={promoting && onPromoteEnd ? onPromoteEnd : undefined}
    >
      {/* Card content. While inert (collapsed deck) it's pointer-through so any
          click lands on the wrapper and expands — the whole card is the target. */}
      <div className={cn(inert && 'pointer-events-none')}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {isFront && (
              <div
                className={cn(
                  'mb-1 text-[10px] font-extrabold uppercase tracking-[.08em] font-mono',
                  labelTone,
                )}
              >
                {descriptor.frontLabel}
              </div>
            )}
            <div className="text-[14px] font-bold leading-snug text-slate-900">{descriptor.title}</div>
            <div className="mt-1 text-[12.5px] leading-relaxed text-slate-500">{descriptor.detail}</div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {/* Always-visible count chip — the count that doesn't depend on hover. */}
            {inert && (
              <span
                data-testid="action-queue-count-chip"
                className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500"
              >
                +{hiddenCount} more
              </span>
            )}
            {descriptor.dismiss && (
              <button
                type="button"
                data-testid={`action-queue-dismiss-${descriptor.id}`}
                aria-label={descriptor.dismiss.label}
                aria-hidden={inert || undefined}
                tabIndex={inert ? -1 : undefined}
                onClick={() => setDismissConfirmOpen(true)}
                className="rounded p-0.5 text-slate-400 hover:text-slate-700"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        </div>
        {actionNode && <div className="mt-3 flex items-center gap-2">{actionNode}</div>}
      </div>

      {descriptor.dismiss && (
        <QueueConfirmDialog
          open={dismissConfirmOpen}
          onOpenChange={setDismissConfirmOpen}
          copy={descriptor.dismiss.confirm}
          onConfirm={() => descriptor.dismiss!.onAccept(resolve)}
        />
      )}

      {/* Hover/focus tint — the ONLY absolute positioning. Hidden by default,
          fades in when the deck wrapper is hovered (fine-pointer only) or
          focus-visible. Pointer-inert so it never blocks the fall-through click. */}
      {inert && (
        <div
          data-testid="action-queue-tint"
          aria-hidden
          className="action-queue-tint pointer-events-none absolute inset-0 flex items-center justify-center rounded-xl bg-slate-900/40 text-sm font-medium text-white opacity-0"
        >
          Click to expand — {hiddenCount} more waiting
        </div>
      )}
    </div>
  );
}

// ── Descriptor assembly + row actions: extracted ────────────────────────────
// `useActionQueueDescriptors` (membership/ranking/mutation wiring) and the
// RapidAction / DailyAction components live in their own modules now — this
// file is only the deck/expand presentation shell.


// ── Local motion + tint CSS (never index.css) ───────────────────────────────
// transform / opacity ONLY. The expand entrance fades each revealed card up from
// translateY(-4px) with a per-index stagger (via the --enter-delay CSS var).
// Collapse just unmounts the cards — no exit choreography. The promote/exit
// keyframes keep the repo's ease-out-quint curve. The hover tint fades in only
// on fine-pointer hover (so touch doesn't get a stuck scrim) or on keyboard
// focus-visible (not gated). Reduced-motion drops the entrance to an opacity-only
// fade and keeps the reduced-motion promote override.
const ENTER_CURVE = 'cubic-bezier(0.23, 1, 0.32, 1)';
const QUEUE_STYLE = `
.action-queue-card {
  transition: opacity 250ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 250ms cubic-bezier(0.22, 1, 0.36, 1);
}
.action-queue-card-exiting {
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 150ms cubic-bezier(0.22, 1, 0.36, 1),
              transform 150ms cubic-bezier(0.22, 1, 0.36, 1);
}
.action-queue-card-promoting {
  animation: action-queue-promote 250ms cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes action-queue-promote {
  from { transform: translateY(8px) scale(0.97); opacity: 0.6; }
  to { transform: none; opacity: 1; }
}

/* Expand entrance: each revealed card fades up from translateY(-4px) to
   identity, staggered by --enter-delay so they cascade in. */
.action-queue-card-entering {
  animation: action-queue-enter 200ms ${ENTER_CURVE} both;
  animation-delay: var(--enter-delay, 0ms);
}
@keyframes action-queue-enter {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: none; }
}

/* Hover/focus tint on the collapsed deck's front card. Opacity-only, transitions
   over 150ms. Revealed on real hover (fine pointer) — so touch devices never get
   a stuck scrim — and on keyboard focus-visible (ungated). */
.action-queue-tint {
  transition: opacity 150ms ease-out;
}
@media (hover: hover) and (pointer: fine) {
  .action-queue-deck:hover .action-queue-tint {
    opacity: 1;
  }
}
.action-queue-deck:focus-visible .action-queue-tint {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .action-queue-card,
  .action-queue-card-exiting {
    transition-property: opacity;
  }
  /* Entrance becomes an opacity-only fade — no transform. */
  @keyframes action-queue-enter {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes action-queue-promote {
    from { opacity: 0.6; }
    to { opacity: 1; }
  }
}
`;
